import React, { useEffect, useState } from 'react';

export function TermsOfUse({ onClose }: { onClose: () => void }) {
  const [content, setContent] = useState<string>('');

  useEffect(() => {
    fetch('/TERMS.md')
      .then(response => response.text())
      .then(text => setContent(text))
      .catch(error => console.error('Error loading terms:', error));
  }, []);

  // Simple markdown to HTML converter for basic formatting
  const renderMarkdown = (text: string) => {
    const lines = text.split('\n');
    const elements: React.ReactElement[] = [];
    let listItems: React.ReactElement[] = [];
    
    const flushList = (index: number) => {
      if (listItems.length > 0) {
        elements.push(<ul key={`list-${index}`} className="ml-6 mb-4 list-disc">{listItems}</ul>);
        listItems = [];
      }
    };
    
    lines.forEach((line, index) => {
      // H1
      if (line.startsWith('# ')) {
        flushList(index);
        elements.push(<h1 key={index} className="text-3xl font-bold mb-4 mt-8 text-gray-900">{line.substring(2)}</h1>);
        return;
      }
      // H2
      if (line.startsWith('## ')) {
        flushList(index);
        elements.push(<h2 key={index} className="text-2xl font-semibold mb-3 mt-6 text-gray-800">{line.substring(3)}</h2>);
        return;
      }
      // H3
      if (line.startsWith('### ')) {
        flushList(index);
        elements.push(<h3 key={index} className="text-xl font-semibold mb-2 mt-4 text-gray-800">{line.substring(4)}</h3>);
        return;
      }
      // List items
      if (line.startsWith('- ')) {
        const content = line.substring(2);
        listItems.push(<li key={index} className="mb-1 text-gray-700">{renderInlineMarkdown(content)}</li>);
        return;
      }
      // Horizontal rule
      if (line.trim() === '---') {
        flushList(index);
        elements.push(<hr key={index} className="my-6 border-gray-300" />);
        return;
      }
      // Empty line
      if (line.trim() === '') {
        flushList(index);
        elements.push(<div key={index} className="h-2" />);
        return;
      }
      // Regular paragraph
      flushList(index);
      elements.push(<p key={index} className="mb-2 text-gray-700 leading-relaxed">{renderInlineMarkdown(line)}</p>);
    });
    
    flushList(lines.length);
    return elements;
  };
  
  // Handle inline markdown like bold, italic, links
  const renderInlineMarkdown = (text: string): (string | React.ReactElement)[] => {
    const parts: (string | React.ReactElement)[] = [];
    let remaining = text;
    let key = 0;
    
    while (remaining.length > 0) {
      // Bold **text**
      const boldMatch = remaining.match(/\*\*([^*]+)\*\*/);
      if (boldMatch && boldMatch.index !== undefined) {
        if (boldMatch.index > 0) {
          parts.push(remaining.substring(0, boldMatch.index));
        }
        parts.push(<strong key={key++} className="font-semibold">{boldMatch[1]}</strong>);
        remaining = remaining.substring(boldMatch.index + boldMatch[0].length);
        continue;
      }
      
      // No more markdown, add remaining text
      parts.push(remaining);
      break;
    }
    
    return parts;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[10000] flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h1 className="text-2xl font-bold text-gray-900">Terms of Use</h1>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Close"
          >
            <svg className="h-6 w-6 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {content ? (
            <div className="prose prose-sm max-w-none">
              {renderMarkdown(content)}
            </div>
          ) : (
            <div className="flex items-center justify-center h-32">
              <p className="text-gray-500">Loading terms...</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
